import React, { useEffect, useState, useRef } from "react";
import { CloudUpload, Trash2, Upload, FileIcon, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
    uploadPurchaseDocument,
    listPurchaseDocument,
    deletePurchaseDocument,
} from "@/redux/Purchase/actions";
import { GetDocumentTypeList } from "@/redux/Common/actions";
import { showToast } from "@/redux/Toast/actions";
import CelebrationButton from "../ui/CelebrationButton/CelebrationButton";



const getDocumentTypeId = (item) => item?.id ?? item?.Id ?? "";

const getDocumentTypeValue = (item) =>
    item?.documentValue ?? item?.DocumentValue ?? getDocumentTypeId(item);

const getDocumentTypeName = (item) =>
    item?.documentName ??
    item?.DocumentName ??
    item?.documentType ??
    item?.DocumentType ??
    item?.Name ??
    item?.name ??
    "";

const MODULE_TYPE_MAP = {
    purchase: 0,
    sales: 0,
    loadplan: 0,
    booking: "OES",
    rateQuote: "OER",
    importRateQuote: "OIR",
    ImportBooking: "OIS",
};

const FileUpload = ({ orderId, orderNum, type }) => {
    const dispatch = useDispatch();
    const [documentType, setDocumentType] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileList, setFileList] = useState([]);
    const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);  // NEW
    const fileInputRef = useRef(null);                     // NEW
    const dragCounter = useRef(0);                         // NEW — tracks nested drag events
    const { isAnimate } = useSelector((state) => state.toast);

    const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? "[{}]");
    const companyId = authUser?.companyId;
    const user = authUser?.firstName + ' ' + authUser?.lastName;

    const url = {
        purchase: {
            list: "v1/PurchaseOrder/GetPurchaseOrderDocsByID?PurchaseOrderID=",
            upload: "v1/PurchaseOrder/UploadPODocument",
            delete: "v1/PurchaseOrder/DeletePoDocs",
        },
        sales: {
            list: "v1/SalesOrder/GetSalesOrderDocsByID?SalesOrderID=",
            upload: "v1/SalesOrder/UploadSODocument",
            delete: "v1/SalesOrder/DeleteSoDocs",
        },
        loadplan: {
            list: "v1/LoadPlan/GetLoadPlanDocsByID?LoadPlanID=",
            upload: "v1/LoadPlan/UploadLPDocument",
            delete: "v1/LoadPlan/DeleteLPDocs",
        },
        booking: {
            list: "v1/Booking/GetBookingDocsByID?BookingID=",
            upload: "v1/Booking/UploadBookingDocument",
            delete: "v1/Booking/DeleteBookingDocs",
        },
        rateQuote: {
            list: "v1/RateQuote/GetRateQuoteDocsByID?RateQuoteID=",
            upload: "v1/RateQuote/UploadRQDocument",
            delete: "v1/RateQuote/DeleteRQDocs",
        },
        importRateQuote: {
            list: "v1/RateQuoteImport/GetImportRateQuoteDocsByID?ImportRateQuoteID=",
            upload: "v1/RateQuoteImport/UploadImportRQDocument",
            delete: "v1/RateQuoteImport/DeleteImportRateQuoteDocs",
        },
        ImportBooking: {
            list: "v1/Arrival_Import/GetBookingDocsByID?BOLID=",
            upload: "v1/Arrival_Import/UploadBookingDocument",
            delete: "v1/ImportBooking/DeleteImportBookingDocs",
        },
    };

    useEffect(() => {
        _getFileList();
    }, [orderId]);

    useEffect(() => {
        GetDocumentTypeList(MODULE_TYPE_MAP[type] ?? 0)
            .then((list) => {
                setDocumentTypeOptions(Array.isArray(list) ? list : []);
            })
            .catch(() => {
                setDocumentTypeOptions([]);
            });
    }, [type]);

    const resolveDocumentTypeName = (idOrName) => {
        const key = String(idOrName ?? "").trim();
        if (!key) return "";

        const match = documentTypeOptions.find(
            (item) =>
                String(getDocumentTypeId(item)) === key ||
                String(getDocumentTypeValue(item)) === key,
        );
        if (match) return getDocumentTypeName(match);

        return key;
    };

    const _getFileList = () => {
        let payload = orderId;
        if (type === "ImportBooking") {
            payload = `${orderId}&CompanyID=${companyId}`;
        }
        setLoading(true);
        listPurchaseDocument(url[type].list, payload)
            .then((response) => {
                setLoading(false);
                if (response && response.statuscode === 200) {
                    setFileList(response.data || []);
                } else {
                    setFileList([]);
                }
            })
            .catch(() => setLoading(false));
    };

    // ─── Drag & Drop Handlers ──────────────────────────────────────────────────

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current += 1;
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setIsDragging(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current -= 1;
        if (dragCounter.current === 0) {
            setIsDragging(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        dragCounter.current = 0;

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            setSelectedFile(files[0]);
            e.dataTransfer.clearData();
        }
    };

    const handleClearFile = () => {
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // ──────────────────────────────────────────────────────────────────────────

    const _saveFile = () => {
        if (!documentType) {
            dispatch(showToast("Please select a document type.", "error"));
            return;
        }
        if (!selectedFile) {
            dispatch(showToast("Please select a file to upload.", "error"));
            return;
        }

        const formData = new FormData();
        if (type === "purchase") {
            formData.append("PurchaseOrderID", orderId);
            formData.append("PoNumber", orderNum);
        } else if (type === "sales") {
            formData.append("SalesOrderID", orderId);
            formData.append("SoNumber", orderNum);
        } else if (type === "loadplan") {
            formData.append("LoadPlanID", orderId);
            formData.append("LPNumber", orderNum);
        } else if (type === "booking") {
            formData.append("BookingID", orderId);
            formData.append("RefNo", orderNum);
        } else if (type === "rateQuote") {
            formData.append("RATEQUOTE_ID", orderId);
            formData.append("RATEQUOTE_NUMBER", orderNum);
        } else if (type === "importRateQuote") {
            formData.append("RATEQUOTE_ID", orderId);
            formData.append("RATEQUOTE_NUMBER", orderNum);
        } else if (type === "ImportBooking") {
            formData.append("BOLID", orderId);
            formData.append("CompanyID", companyId);
            formData.append("RefNo", orderNum);
        }
        formData.append("DocumentType", documentType);
        formData.append("FileData", selectedFile);
        formData.append("CreatedBy", "ADMIN");

        setUploadLoading(true);
        uploadPurchaseDocument(url[type].upload, formData)
            .then((response) => {
                setUploadLoading(false);
                if (response && (response.statuscode === 200 || response.statuscode === 201)) {
                    dispatch(showToast("File uploaded successfully.", "success", true));
                    setDocumentType("");
                    handleClearFile();
                    _getFileList();
                } else {
                    dispatch(showToast("Failed to upload file. Please try again.", "error"));
                    setDocumentType("");
                    handleClearFile();
                }
            })
            .catch(() => {
                setUploadLoading(false);
                dispatch(showToast("An error occurred while uploading the file.", "error"));
            });
    };

    const _deleteFile = (fileId) => {
        let payload = { id: fileId, modifiedBy: "PSR" };
        if (type === "ImportBooking") {
            payload = { id: fileId, CompanyID: companyId, ModifiedBy: user };
        }
        setLoading(true);
        deletePurchaseDocument(url[type].delete, payload)
            .then((response) => {
                setLoading(false);
                if ((response && response.statuscode === 200) || response.statuscode === 201) {
                    dispatch(showToast("File deleted successfully.", "success"));
                    _getFileList();
                } else {
                    dispatch(showToast("Failed to delete file. Please try again.", "error"));
                }
            })
            .catch(() => {
                setLoading(false);
                dispatch(showToast("An error occurred while deleting the file.", "error"));
            });
    };

    return (
        <>
            <div className="p-4">
                <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">
                    Upload Documents
                </h2>
                <div className="grid grid-cols-2 gap-4">
                    {/* Document Type */}
                    <div>
                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                            Document Type <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={documentType}
                            onChange={(e) => setDocumentType(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-1 focus:ring-[#0072FF] focus:border-[#0072FF]"
                        >
                            <option value="">SELECT DOCUMENT TYPE</option>
                            {documentTypeOptions.map((item) => {
                                const id = getDocumentTypeId(item);
                                const value = getDocumentTypeValue(item);
                                const name = getDocumentTypeName(item);
                                if (!value || !name) return null;
                                return (
                                    <option key={id || value} value={String(value)}>
                                        {name}
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    {/* File Drop Zone */}
                    <div>
                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                            Attach File <span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center space-x-2">
                            {/* ── Drop Zone ── */}
                            <div
                                onDragEnter={handleDragEnter}
                                onDragLeave={handleDragLeave}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                onClick={() => !selectedFile && fileInputRef.current?.click()}
                                className={`
                                    flex-1 relative flex items-center gap-2 px-3 py-2 text-sm rounded-lg border
                                    transition-all duration-200 cursor-pointer
                                    ${isDragging
                                        ? "border-[#0072FF] bg-blue-50 dark:bg-blue-950 border-dashed scale-[1.01]"
                                        : selectedFile
                                            ? "border-green-400 bg-green-50 dark:bg-green-950 dark:border-green-700 cursor-default"
                                            : "border-slate-300 dark:border-slate-700 border-dashed bg-white dark:bg-slate-800 hover:border-[#0072FF] hover:bg-blue-50 dark:hover:bg-slate-700"
                                    }
                                `}
                            >
                                {/* Hidden native input */}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    onChange={(e) => setSelectedFile(e.target.files[0])}
                                    className="hidden"
                                />

                                {selectedFile ? (
                                    /* ── File chip when a file is chosen ── */
                                    <>
                                        <FileIcon className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" />
                                        <span className="flex-1 truncate text-xs text-green-700 dark:text-green-300 font-medium">
                                            {selectedFile.name}
                                        </span>
                                        <span className="text-xs text-slate-400 shrink-0">
                                            {(selectedFile.size / 1024).toFixed(1)} KB
                                        </span>
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); handleClearFile(); }}
                                            className="shrink-0 p-0.5 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900 transition-colors"
                                            title="Remove file"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </>
                                ) : (
                                    /* ── Idle / dragging placeholder ── */
                                    <>
                                        <Upload className={`w-4 h-4 shrink-0 transition-colors ${isDragging ? "text-[#0072FF]" : "text-slate-400"}`} />
                                        <span className={`text-xs transition-colors ${isDragging ? "text-[#0072FF] font-medium" : "text-slate-400"}`}>
                                            {isDragging ? "Drop file here" : "Click or drag & drop a file"}
                                        </span>
                                    </>
                                )}
                            </div>

                            <CelebrationButton
                                onClick={_saveFile}
                                label="Upload File"
                                className="px-4 py-2 text-sm bg-[#0072FF] hover:bg-[#0062DD] text-white rounded-lg font-medium transition-all whitespace-nowrap"
                                isLoading={uploadLoading}
                                icon={<CloudUpload className="w-4 h-4" />}
                                isSuccess={isAnimate}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Uploaded Documents Table */}
            <div className="p-4">
                <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">
                    Uploaded Documents
                </h2>
                <div className="mt-4 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 p-4">
                    {fileList.length === 0 ? (
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            No documents uploaded yet.
                        </p>
                    ) : (
                        <table className="w-full text-sm text-left text-slate-900 dark:text-white">
                            <thead>
                                <tr>
                                    <th className="px-3 py-2 border-b border-slate-200 dark:border-slate-800">Document Type</th>
                                    <th className="px-3 py-2 border-b border-slate-200 dark:border-slate-800">File Name</th>
                                    <th className="px-3 py-2 border-b border-slate-200 dark:border-slate-800">Uploaded On</th>
                                    <th className="px-3 py-2 border-b border-slate-200 dark:border-slate-800">Uploaded By</th>
                                    <th className="px-3 py-2 border-b border-slate-200 dark:border-slate-800">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={5}>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">Loading...</p>
                                        </td>
                                    </tr>
                                ) : (
                                    fileList.map((file, index) => (
                                        <tr key={index} className="hover:bg-slate-100 dark:hover:bg-slate-800">
                                            <td className="px-3 py-2 border-b border-slate-200 dark:border-slate-800">
                                                {resolveDocumentTypeName(
                                                    file?.documentType ?? file?.documentTypeId,
                                                )}
                                            </td>
                                            <td className="px-3 py-2 border-b border-slate-200 dark:border-slate-800">
                                                <a href={file.filePath} target="_blank" rel="noopener noreferrer" className="text-indigo-400">
                                                    {file.fileName}
                                                </a>
                                            </td>
                                            <td className="px-3 py-2 border-b border-slate-200 dark:border-slate-800">
                                                {moment(file.uploadedOn).format("DD-MM-YYYY")}
                                            </td>
                                            <td className="px-3 py-2 border-b border-slate-200 dark:border-slate-800">{file.createdBy}</td>
                                            <td className="px-3 py-2 border-b border-slate-200 dark:border-slate-800">
                                                <button
                                                    className="p-1 text-slate-500 hover:text-red-600 transition-colors"
                                                    title="Delete"
                                                    onClick={() => _deleteFile(file?.id)}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </>
    );
};

export default FileUpload;